import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Textarea,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useResetPlatformUserMfa } from '@/hooks/useAdminData'
import { useToast } from '@/hooks/useToast'
import { authService } from '@/services/authService'

interface PlatformUserMfaResetTarget {
  id: string
  email?: string
  username?: string
}

interface PlatformUserMfaResetModalProps {
  isOpen: boolean
  onClose: () => void
  user: PlatformUserMfaResetTarget | null
  onSuccess: () => void
}

export function PlatformUserMfaResetModal({
  isOpen,
  onClose,
  user,
  onSuccess,
}: PlatformUserMfaResetModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const resetMfaMutation = useResetPlatformUserMfa()
  const [totp, setTotp] = useState('')
  const [reason, setReason] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const isSubmitting = isVerifying || resetMfaMutation.isPending

  const close = () => {
    if (isSubmitting) {
      return
    }
    setTotp('')
    setReason('')
    onClose()
  }

  const handleReset = async () => {
    if (!user || !/^\d{6}$/.test(totp) || !reason.trim()) {
      return
    }
    setIsVerifying(true)
    try {
      await authService.verifyMfa(totp)
      await resetMfaMutation.mutateAsync({
        userId: user.id,
        reason: reason.trim(),
      })
      toast.success({
        title: t('users.mfaReset.success', 'MFA reset'),
        description: t(
          'users.mfaReset.successDescription',
          'The user must enroll MFA again at next sign-in.'
        ),
      })
      setTotp('')
      setReason('')
      onSuccess()
      onClose()
    } catch (error) {
      const responseData = (error as any)?.response?.data
      toast.error({
        title: t('users.mfaReset.failed', 'MFA reset failed'),
        description:
          responseData?.error?.message ??
          responseData?.message ??
          t('users.mfaReset.failedDescription', 'Verify your code and try again.'),
      })
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="md">
        <ModalHeader>{t('users.mfaReset.title', 'Reset MFA')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Alert status="warning" alignItems="flex-start">
              <AlertIcon />
              <AlertDescription>
                {t('users.mfaReset.warning', {
                  email: user?.email ?? user?.username ?? user?.id ?? '',
                  defaultValue:
                    'This resets MFA for {{email}} and revokes their merchant sessions.',
                })}
              </AlertDescription>
            </Alert>
            <FormControl isRequired>
              <FormLabel>{t('users.mfaReset.yourCode', 'Your MFA code')}</FormLabel>
              <Input
                value={totp}
                onChange={(event) =>
                  setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder={t('auth.mfa.codePlaceholder')}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t('users.mfaReset.reason', 'Reason')}</FormLabel>
              <Textarea
                value={reason}
                onChange={(event) => setReason(event.target.value.slice(0, 500))}
                placeholder={t('users.mfaReset.reasonPlaceholder', 'Explain why MFA is reset')}
                maxLength={500}
                resize="vertical"
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={close} isDisabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="red"
            onClick={handleReset}
            isLoading={isSubmitting}
            isDisabled={!/^\d{6}$/.test(totp) || !reason.trim()}
          >
            {t('users.mfaReset.action', 'Reset MFA')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
